import { useEffect } from 'react';

import { env } from './env';
import { clearStoredSession, readStoredSession, storeRotatedTokens, useSession } from './session';

/**
 * Silent renewal of the merchant session.
 *
 * Root cause of the « déconnexions répétées »: the access token lives `ACCESS_TOKEN_TTL_MINUTES` (15 min by default)
 * and nothing ever renewed it, so the first call after a quarter of an hour answered 401 `SESSION_EXPIRED` and
 * `api-client.ts` signed the merchant out — while the 30-day refresh token sat unused in the session.
 *
 * Now the pair is rotated through `POST /auth/refresh-token`:
 *   - BEFORE the access token expires (`useSessionKeepAlive`, one minute ahead, and again when a sleeping tab wakes);
 *   - and once on a `SESSION_EXPIRED` 401 (`apiRequest` retries the call with the new token).
 * Only the API refusing the refresh token (401/400: revoked, expired, signed out elsewhere) ends the session. A network
 * failure or a 5xx never does: the session is not proven dead, the renewal is simply tried again.
 *
 * The API revokes the WHOLE session when a refresh token is presented twice (reuse detection,
 * `session.service.ts`). So a rotation must never run twice for one token:
 *   - one renewal at a time per tab (`inFlight`), shared by every caller;
 *   - one at a time across tabs (Web Locks), since a duplicated tab starts with a copy of the same `sessionStorage`;
 *   - the tab that rotated tells the others (BroadcastChannel), and a tab still holding the rotated-away token adopts
 *     the new pair instead of presenting the old one.
 */
export type RefreshOutcome =
  | { readonly kind: 'refreshed'; readonly accessToken: string }
  | { readonly kind: 'rejected' }
  | { readonly kind: 'unavailable' };

const LOCK_NAME = 'souk-merchant-token-refresh';
const CHANNEL_NAME = 'souk-merchant-session';
/** Renew this long before the access token expires. */
const RENEW_BEFORE_EXPIRY_MS = 60_000;
/** After a failed renewal that did not prove the session dead (network, 5xx). */
const RETRY_AFTER_FAILURE_MS = 30_000;
/** A renewal call taking longer than this is abandoned (counted as a temporary failure). */
const REFRESH_TIMEOUT_MS = 15_000;
/** Holds the lock a moment after broadcasting, so a waiting tab has adopted the new pair before it takes its turn. */
const BROADCAST_SETTLE_MS = 150;

interface RotationMessage {
  readonly type: 'rotated';
  /** The refresh token that was rotated away: only a tab still holding exactly this one adopts the new pair. */
  readonly from: string;
  readonly accessToken: string;
  readonly refreshToken: string;
  readonly mustChangePassword: boolean;
}

function isRotationMessage(value: unknown): value is RotationMessage {
  if (typeof value !== 'object' || value === null) return false;
  const message = value as Partial<RotationMessage>;
  return (
    message.type === 'rotated' &&
    typeof message.from === 'string' &&
    typeof message.accessToken === 'string' &&
    typeof message.refreshToken === 'string' &&
    typeof message.mustChangePassword === 'boolean'
  );
}

let channel: BroadcastChannel | null | undefined;

function sessionChannel(): BroadcastChannel | null {
  if (channel === undefined) {
    channel = typeof BroadcastChannel === 'undefined' ? null : new BroadcastChannel(CHANNEL_NAME);
    channel?.addEventListener('message', (event: MessageEvent<unknown>) => {
      const message = event.data;
      if (!isRotationMessage(message)) return;
      if (readStoredSession().refreshToken !== message.from) return;
      storeRotatedTokens({
        accessToken: message.accessToken,
        refreshToken: message.refreshToken,
        mustChangePassword: message.mustChangePassword,
      });
    });
  }
  return channel;
}

/** `exp` of a JWT, in ms, read without verifying it (only to plan the renewal; the API verifies). `null` if unreadable. */
export function tokenExpiresAt(token: string): number | null {
  const payload = token.split('.')[1];
  if (payload === undefined) return null;
  try {
    const json = atob(payload.replace(/-/gu, '+').replace(/_/gu, '/'));
    const claims: unknown = JSON.parse(json);
    if (typeof claims !== 'object' || claims === null || !('exp' in claims)) return null;
    const exp = Number(claims.exp);
    return Number.isFinite(exp) ? exp * 1000 : null;
  } catch {
    return null;
  }
}

function withLock<T>(task: () => Promise<T>): Promise<T> {
  const locks = typeof navigator === 'undefined' ? undefined : navigator.locks;
  // `navigator.locks` exists in every supported browser; absent (tests, very old engines) the per-tab guard remains.
  if (locks === undefined || typeof locks.request !== 'function') return task();
  // The DOM typing says `Promise<Promise<T>>`; the lock resolves with the callback's own (awaited) result.
  return locks.request(LOCK_NAME, task) as unknown as Promise<T>;
}

async function rotate(staleAccessToken: string | null): Promise<RefreshOutcome> {
  const stored = readStoredSession();
  if (stored.accessToken === null || stored.refreshToken === null) return { kind: 'rejected' };
  // Renewed meanwhile (an earlier call, or another tab's broadcast while this one waited for the lock).
  if (staleAccessToken !== null && stored.accessToken !== staleAccessToken) {
    return { kind: 'refreshed', accessToken: stored.accessToken };
  }

  let response: Response;
  try {
    response = await fetch(`${env.apiBaseUrl}/api/v1/auth/refresh-token`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ refreshToken: stored.refreshToken }),
      cache: 'no-store',
      // A renewal that never answers must not hold every waiting call (and the cross-tab lock) forever.
      signal: AbortSignal.timeout(REFRESH_TIMEOUT_MS),
    });
  } catch {
    return { kind: 'unavailable' };
  }
  if (response.status === 401 || response.status === 400) return { kind: 'rejected' };
  if (!response.ok) return { kind: 'unavailable' };

  const payload: unknown = await response.json().catch(() => null);
  if (
    typeof payload !== 'object' ||
    payload === null ||
    !('accessToken' in payload) ||
    !('refreshToken' in payload) ||
    typeof payload.accessToken !== 'string' ||
    typeof payload.refreshToken !== 'string'
  ) {
    return { kind: 'unavailable' };
  }
  const rotated = {
    accessToken: payload.accessToken,
    refreshToken: payload.refreshToken,
    mustChangePassword: 'mustChangePassword' in payload && payload.mustChangePassword === true,
  };
  // Signed out while the request was in flight: the answer is dropped, the session stays closed.
  if (!storeRotatedTokens(rotated)) return { kind: 'rejected' };

  const announce = sessionChannel();
  if (announce !== null) {
    announce.postMessage({
      type: 'rotated',
      from: stored.refreshToken,
      ...rotated,
    } satisfies RotationMessage);
    await new Promise((resolve) => setTimeout(resolve, BROADCAST_SETTLE_MS));
  }
  return { kind: 'refreshed', accessToken: rotated.accessToken };
}

let inFlight: Promise<RefreshOutcome> | null = null;

/**
 * Renews the session; `staleAccessToken` is the token the caller found expired (or about to). Every concurrent caller
 * shares the same renewal.
 */
export function refreshSession(staleAccessToken: string | null): Promise<RefreshOutcome> {
  inFlight ??= withLock(() => rotate(staleAccessToken)).finally(() => {
    inFlight = null;
  });
  return inFlight;
}

/** The session is really over (refresh refused): purge it and go to the sign-in page. */
export function endSessionAndRedirect(): void {
  clearStoredSession();
  if (window.location.pathname !== '/login') window.location.assign('/login');
}

/**
 * Keeps the signed-in session alive: renews the access token a minute before it expires (and as soon as a sleeping
 * tab becomes visible again, background timers being throttled). Mounted once, under `SessionProvider`.
 */
export function useSessionKeepAlive(): void {
  const { accessToken } = useSession();

  useEffect(() => {
    sessionChannel(); // listen to other tabs' rotations even if this one never renews itself
    if (accessToken === null) return undefined;
    let stopped = false;
    let timer: ReturnType<typeof setTimeout> | undefined;

    const renew = async (): Promise<void> => {
      const outcome = await refreshSession(accessToken);
      if (stopped) return;
      if (outcome.kind === 'rejected') endSessionAndRedirect();
      else if (outcome.kind === 'unavailable')
        timer = setTimeout(() => void renew(), RETRY_AFTER_FAILURE_MS);
      // 'refreshed': the new token re-runs this effect, which plans the next renewal.
    };
    const plan = (): void => {
      clearTimeout(timer);
      const expiresAt = tokenExpiresAt(accessToken);
      if (expiresAt === null) return;
      timer = setTimeout(
        () => void renew(),
        Math.max(0, expiresAt - Date.now() - RENEW_BEFORE_EXPIRY_MS),
      );
    };
    const onVisibility = (): void => {
      if (document.visibilityState === 'visible') plan();
    };

    plan();
    document.addEventListener('visibilitychange', onVisibility);
    return () => {
      stopped = true;
      clearTimeout(timer);
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, [accessToken]);
}

/** Renders nothing: hosts `useSessionKeepAlive` for every route (the shell AND the password-change screen). */
export function SessionKeepAlive(): null {
  useSessionKeepAlive();
  return null;
}
